import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { CODLogoMark, CODMonogram, ChicagoStars, ChicagoStar, SkylineDivider } from "../components/cod/Logo";
import { PublicHeader } from "../components/cod/PublicHeader";
import { FlyerCarousel } from "../components/cod/FlyerCarousel";
import { Seo } from "../components/cod/Seo";
import { Button } from "../components/ui/button";
import { IMG } from "../lib/images";
import { ArrowRight, Phone, MessageSquare, Users, Briefcase, Heart, CheckCircle2, Award, ShieldCheck, Compass, Building2 } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Seo
        title={null}
        description="Chicago Opportunity Desk (COD) connects Chicago residents to employment, healthcare careers, certifications, housing resources, government benefits, and workforce opportunities through one intelligent platform."
        path="/"
        schema="LocalBusiness"
      />
      <PublicHeader/>

      {/* BRIGHT HERO — white background, photo card on right, navy headline */}
      <section className="relative bg-white overflow-hidden">
        <div className="absolute -top-20 -right-20 opacity-[0.06]" aria-hidden>
          <CODMonogram size={520} color="var(--cod-navy)"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider"
                 style={{background:"var(--cod-red)", color:"#ffffff"}}
                 data-testid="hero-phase-badge">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse"/>
              Employment & Staffing · Available Now
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] text-slate-900">
              Connect <span style={{color:"var(--cod-navy)"}}>People</span>. Connect <span style={{color:"var(--cod-navy)"}}>Employers</span>.<br/>
              Create <span style={{color:"var(--cod-red)"}}>Opportunity</span>.
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-slate-700 max-w-2xl leading-relaxed">
              Chicago Opportunity Desk is launching in phases. <strong>Employment and staffing services are available now</strong> — connecting job seekers with opportunities and helping employers identify qualified talent. Additional workforce, certification, training, business, and community services are being introduced as our network and capacity expand.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button size="lg" onClick={()=>navigate("/start?intent=find_work")} data-testid="find-employment-btn"
                className="bg-[var(--cod-red)] hover:bg-[#A40D26] text-white text-base px-8 py-7 rounded-md">
                Find Employment <ArrowRight className="ml-2" size={18}/>
              </Button>
              <Button size="lg" variant="outline" onClick={()=>navigate("/employers")} data-testid="find-talent-btn"
                className="text-base px-8 py-7 rounded-md border-[var(--cod-navy)] text-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)]">
                Find Talent
              </Button>
            </div>
            <p className="mt-6 text-sm text-slate-500">Looking for Work? Join our Candidate Network. Looking for Talent? Partner with COD.</p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-lg overflow-hidden shadow-xl border border-slate-200">
              <img src={IMG.heroPeople} alt="Chicago professionals" fetchPriority="high" decoding="async" className="w-full h-[480px] object-cover"/>
              <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur p-5 border-t-4 border-[var(--cod-red)]">
                <div className="flex items-center gap-3">
                  <CODLogoMark size={44} onLight/>
                  <div>
                    <div className="font-serif text-base font-semibold text-slate-900">Serving Chicago</div>
                    <div className="text-xs text-slate-600 uppercase tracking-widest">Workforce · Education · Stability</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SkylineDivider color="var(--cod-navy)" className="opacity-90"/>
      </section>

      {/* FLYER CAROUSEL — employer partner flyers with auto-advance */}
      <FlyerCarousel/>

      {/* WHO WE SERVE */}
      <section className="cod-section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="cod-eyebrow">Who We Serve</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-3">A trusted partner for residents, employers, and the organizations that serve them.</h2>
            <div className="cod-rule-red mt-6"/>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              {icon: Users,     t:"Residents",             d:"Adults seeking work, training, certifications, housing, or stability supports.", img: IMG.community,       to: "/pathways",  cta: "Explore career pathways", testid: "who-residents"},
              {icon: Briefcase, t:"Employers",             d:"Chicago employers building diverse, prepared talent pipelines.",                  img: IMG.interview,       to: "/employers", cta: "Partner with COD",         testid: "who-employers"},
              {icon: Heart,     t:"Partner Organizations", d:"Community-based and government partners delivering shared outcomes.",             img: IMG.careerCoaching,  to: "/partners",  cta: "See partnership options",  testid: "who-partners"},
            ].map((b) => (
              <Link
                key={b.t}
                to={b.to}
                data-testid={b.testid}
                className="cod-card cod-card-elev overflow-hidden group hover:shadow-lg transition-shadow"
              >
                <div className="h-48 overflow-hidden"><img src={b.img} alt={b.t} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/></div>
                <div className="p-6">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{ background: "var(--cod-navy-soft)" }}>
                    <b.icon size={20} style={{ color: "var(--cod-navy)" }}/>
                  </div>
                  <h3 className="font-serif text-xl text-slate-900 mb-2">{b.t}</h3>
                  <p className="text-slate-600 leading-relaxed">{b.d}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--cod-red)] group-hover:gap-2 transition-all">
                    {b.cta}
                    <ArrowRight size={14}/>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NAVIGATOR — light tint section */}
      <section className="cod-section cod-section-tint">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="cod-eyebrow">A real conversation, not a form</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-3">Meet the Opportunity Navigator.</h2>
            <div className="cod-rule-red mt-6 mb-6"/>
            <p className="text-lg text-slate-700 leading-relaxed">
              The Navigator is a warm, professional guide who listens, asks the right next question, and builds a personalized plan with you. When something is urgent, a real COD case manager is notified the same day.
            </p>
            <ul className="mt-8 space-y-3">
              {["One question at a time — no forms, no maze",
                "A written plan with specific next steps",
                "Your record, transportation, housing, and family situation are heard",
                "A human case manager picks up where the Navigator left off",
              ].map((l, i) => (
                <li key={l} className="flex gap-3 text-slate-800"><CheckCircle2 size={20} style={{ color: "var(--cod-red)" }} className="mt-0.5 flex-shrink-0"/> {l}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white rounded-lg p-6 lg:p-8 shadow-lg border border-slate-200">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-serif text-lg bg-[var(--cod-navy)]">N</div>
                <div>
                  <div className="font-medium text-slate-900">COD Opportunity Navigator</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full inline-block"/> Online</div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="cod-bubble-ai max-w-[85%]">Hi — I'm the COD Opportunity Navigator. What brings you here today?</div>
                <div className="cod-bubble-user max-w-[80%] ml-auto">I need to find work, but my record is making it hard.</div>
                <div className="cod-bubble-ai max-w-[85%]">Thanks for sharing that. We help with this every day. Can I ask what kind of work you're hoping to find?</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE GROUPS — repository-defined */}
      <section className="cod-section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="cod-eyebrow">Our Services</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-3">Four service groups. One coordinated platform.</h2>
              <div className="cod-rule-red mt-6"/>
            </div>
            <Link to="/services" className="text-sm font-medium flex items-center gap-1 text-[var(--cod-navy)]" data-testid="see-all-services">See all services <ArrowRight size={14}/></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {to:"/services", title:"Employment Services", desc:"Career Navigation, Resume Assistance, Interview Prep, Job Placement, Workforce Readiness.", icon: Briefcase},
              {to:"/services", title:"Employment Requirements", desc:"Fingerprinting, Background Checks, PERC Assistance.", icon: ShieldCheck},
              {to:"/services", title:"Certifications", desc:"CPR/BLS, First Aid, Food Handler, Food Manager, ServSafe Alcohol, Security Training.", icon: Award},
              {to:"/services", title:"Healthcare Pathways", desc:"CNA, PCT, Phlebotomy, EKG, Medical Coding & Billing, Home Care Aide, Career Navigation.", icon: Heart},
              {to:"/services", title:"Business Services", desc:"Gov Contract Accelerator, Business Formation, Capability Statements, Vendor Registration, Procurement Readiness.", icon: Building2},
              {to:"/services", title:"Community Services", desc:"Housing, Public Benefits, Behavioral Health, and Community Resource Referrals.", icon: Compass},
            ].map(c => (
              <Link to={c.to} key={c.title} className="cod-card cod-card-elev overflow-hidden block p-6">
                <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4" style={{ background: "var(--cod-navy-soft)" }}>
                  <c.icon size={20} style={{ color: "var(--cod-navy)" }}/>
                </div>
                <h3 className="font-serif text-xl text-slate-900">{c.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.desc}</p>
                <div className="mt-3 text-sm font-medium flex items-center gap-1 text-[var(--cod-navy)]">Explore <ArrowRight size={14}/></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CIVIC STATS BAND — light, IDHS-style */}
      <section className="cod-section" style={{ background: "var(--cod-navy-soft)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <ChicagoStars size={18} className="mb-4"/>
            <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 leading-tight">Built in Chicago.<br/>For Chicagoans.</h2>
            <p className="mt-5 text-slate-700 max-w-md leading-relaxed">COD is a civic-grade workforce platform built on years of community-rooted design with case managers, employers, and the residents we serve.</p>
            <div className="mt-6"><CODLogoMark size={56} onLight/></div>
          </div>
          {[
            {n:"7", l:"Pathways supported"},
            {n:"Same-day", l:"Response on urgent needs"},
            {n:"100%", l:"Real human follow-through"},
          ].map((s,i)=>(
            <div key={s.l} className="border-t-4 pt-5" style={{ borderColor: "var(--cod-red)" }}>
              <div className="font-serif text-5xl text-[var(--cod-navy)]">{s.n}</div>
              <div className="text-xs text-slate-600 mt-2 uppercase tracking-widest font-semibold">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cod-section bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ChicagoStars className="justify-center mb-6" size={16}/>
          <h2 className="font-serif text-3xl sm:text-5xl text-slate-900 leading-tight">Your next step is one conversation away.</h2>
          <p className="text-lg text-slate-600 mt-5 max-w-2xl mx-auto">No forms first. No qualification gate. Talk with the Navigator and leave today with something real in hand.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button size="lg" onClick={()=>navigate("/start")} className="bg-[var(--cod-red)] hover:bg-[#A40D26] text-white text-base px-8 py-7 rounded-md">
              Talk with the Navigator <MessageSquare className="ml-2" size={18}/>
            </Button>
            <Button size="lg" variant="outline" onClick={()=>navigate("/contact")}
              className="text-base px-8 py-7 rounded-md border-[var(--cod-navy)] text-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)]">
              <Phone size={16} className="mr-2"/> Call (312) 343-4183
            </Button>
          </div>
          <p className="text-sm text-slate-600 mt-4">
            Or email us at <a href="mailto:info@joinCOD.org" className="font-medium underline" style={{color:"var(--cod-navy)"}}>info@joinCOD.org</a>
          </p>
        </div>
      </section>
    </div>
  );
}
